/**
 * The AI credit meter, in the rail.
 *
 * Credits are the one thing in this product that runs out mid-sentence. Burying
 * the balance in Billing means the first time most people see it is the moment
 * the agent stops answering — so it lives where they already are, and it is a
 * BAR rather than a number because "12,480" answers "how many" while the
 * question people actually have is "how much is left".
 *
 * It reuses the `['billing']` query key, so on the Billing page this costs
 * nothing at all and everywhere else it is one cached request per session.
 *
 * Hidden entirely when the plan has no AI — Starter's assistant is genuinely
 * off, and a meter reading 0/0 forever is worse than no meter.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Coins } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useI18n } from '@/contexts/I18nContext';
import { useSidebar } from '@/components/shadcn/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip';
import { cn } from '@/lib/utils';

interface BillingLite {
  entitlements: { plan: { capabilities: { ai: boolean } }; open_trial: boolean };
  usage: { metric: string; used: number; quota: number }[];
  credits: { purchased: { remaining: number } };
}

const AI_METRIC = 'ai_credits';

export function SidebarCredits() {
  const { t, lang } = useI18n();
  const { state, isMobile } = useSidebar();
  const navigate = useNavigate();
  const collapsed = state === 'collapsed' && !isMobile;
  const locale = lang === 'ar' ? 'ar-BH-u-nu-latn' : 'en';

  const { data } = useQuery<BillingLite, ApiError>({
    queryKey: ['billing'],
    queryFn: () => api<BillingLite>('/billing'),
    staleTime: 60_000,
  });

  if (!data?.entitlements.plan.capabilities.ai) return null;

  const ai = data.usage.find((u) => u.metric === AI_METRIC);
  const monthly = Math.max(0, (ai?.quota ?? 0) - (ai?.used ?? 0));
  /* Bought credits never expire and are spent only after the monthly allowance,
     so "left" is honestly the sum — showing just the monthly figure would tell
     somebody they are nearly out while 200,000 sit unused on the account. */
  const purchased = data.credits.purchased.remaining;
  const left = monthly + purchased;
  const quota = (ai?.quota ?? 0) + purchased;
  const pct = quota > 0 ? Math.max(0, Math.min(100, (left / quota) * 100)) : 0;
  const low = pct <= 15;

  if (collapsed) {
    // In the icon rail there is no room for a label — the bar alone still says
    // "running out", which is the only thing worth saying at 48px wide.
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => navigate('/billing')}
            className="mx-auto flex h-8 w-8 items-center justify-center rounded-md hover:bg-sidebar-accent"
            aria-label={t('billing_ai')}
          >
            <span className="relative flex h-1 w-5 overflow-hidden rounded-full bg-sidebar-foreground/15">
              <span
                className={cn('h-full rounded-full', low ? 'bg-destructive' : 'bg-primary')}
                style={{ width: `${pct}%` }}
              />
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side={lang === 'ar' ? 'left' : 'right'}>
          {left.toLocaleString(locale)} {t('billing_credits_left')}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/billing')}
      className="w-full rounded-md px-2 py-2 text-start transition-colors hover:bg-sidebar-accent"
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11.5px] text-sidebar-foreground/60">
          <Coins className="size-3" aria-hidden />
          {t('billing_ai')}
        </span>
        <span
          className={cn(
            'text-[11.5px] font-medium tabular-nums',
            low ? 'text-destructive' : 'text-sidebar-foreground/80',
          )}
        >
          {left.toLocaleString(locale)}
        </span>
      </span>

      <span className="mt-1.5 flex h-1 w-full overflow-hidden rounded-full bg-sidebar-foreground/12">
        <span
          className={cn(
            'h-full rounded-full transition-[width] duration-500',
            low ? 'bg-destructive' : 'bg-primary',
          )}
          style={{ width: `${pct}%` }}
        />
      </span>

      {/*
        The caption has to describe the NUMBER above it, not the plan.

        It said "included in your trial" whenever a trial was open, no matter
        where the balance came from. A workspace whose monthly allowance was
        spent and whose remaining credits were all bought was shown its
        top-up balance under the word "trial" — a true number with a false
        label, which is worse than either alone.
      */}
      <span className="mt-1 block text-[10.5px] text-sidebar-foreground/45">
        {monthly === 0 && purchased > 0
          ? t('billing_credits_from_topup')
          : data.entitlements.open_trial
            ? t('billing_credits_trial')
            : low
              ? t('billing_credits_top_up')
              : t('billing_credits_left')}
      </span>
    </button>
  );
}
